import React, { useRef, useState } from "react";
import { motion, useScroll, useTransform, useSpring, AnimatePresence } from "framer-motion";
import { FadeUp, MaskText } from "@/components/Reveal";
import { FOUNDER, TEAM } from "@/data/content";
import { ChevronDown } from "lucide-react";

export const Team = () => {
    const sectionRef = useRef(null);
    const { scrollYProgress: sectionScroll } = useScroll({
        target: sectionRef,
        offset: ["start end", "end start"],
    });
    const sectionProgress = useSpring(sectionScroll, {
        stiffness: 90,
        damping: 26,
        mass: 0.4,
    });
    const orbY = useTransform(sectionProgress, [0, 1], [-40, 80]);

    return (
        <section
            id="team"
            ref={sectionRef}
            data-testid="team-section"
            className="relative py-28 sm:py-36 lg:py-40 bg-[hsl(var(--bg-elevated))] overflow-hidden"
        >
            <motion.div
                aria-hidden
                style={{ y: orbY }}
                className="absolute top-1/4 -right-32 w-[680px] h-[680px] pointer-events-none"
            >
                <div
                    className="absolute inset-0 rounded-full"
                    style={{
                        background:
                            "radial-gradient(circle, hsla(38,92%,62%,0.10), transparent 60%)",
                        filter: "blur(50px)",
                    }}
                />
            </motion.div>

            <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
                <div className="flex items-baseline justify-between gap-6 mb-14">
                    <FadeUp>
                        <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--text-muted))]">
                            <span className="text-[hsl(var(--accent-gold))]">❖</span> 02 /
                            Team
                        </div>
                    </FadeUp>
                    <FadeUp delay={0.05}>
                        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--text-muted))] text-right">
                            The People Behind the House
                        </div>
                    </FadeUp>
                </div>

                {/* === Featured: B.S. Radhakrishnan === */}
                <FounderBlock />

                {/* === Girishh — second featured === */}
                <div className="mt-28 sm:mt-32 pt-16 border-t border-white/8">
                    <DirectorBlock person={TEAM[0]} />
                </div>

                {/* === Santosh + Pranav — text-only side by side === */}
                <div className="mt-20 grid grid-cols-12 gap-8 lg:gap-10">
                    {TEAM.slice(1).map((p) => (
                        <FadeUp
                            key={p.slug}
                            className="col-span-12 md:col-span-6"
                        >
                            <ExecCard person={p} />
                        </FadeUp>
                    ))}
                </div>
            </div>
        </section>
    );
};

// ===== Featured founder block (B.S.) =====
const FounderBlock = () => {
    const frameRef = useRef(null);
    const sectionRef = useRef(null);
    const quoteRef = useRef(null);

    const { scrollYProgress: blockScroll } = useScroll({
        target: sectionRef,
        offset: ["start end", "end start"],
    });
    const blockProgress = useSpring(blockScroll, {
        stiffness: 90,
        damping: 26,
        mass: 0.4,
    });
    const portraitScale = useTransform(
        blockProgress,
        [0, 0.5, 1],
        [1.04, 1, 0.98]
    );

    const { scrollYProgress: quoteScroll } = useScroll({
        target: quoteRef,
        offset: ["start 0.92", "start 0.5"],
    });
    const quoteSmooth = useSpring(quoteScroll, {
        stiffness: 140,
        damping: 28,
        mass: 0.5,
    });
    const quoteX = useTransform(quoteSmooth, [0, 1], [-30, 0]);
    const quoteOpacity = useTransform(quoteSmooth, [0, 1], [0, 1]);

    const onMove = (e) => {
        const el = frameRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const x = ((e.clientX - r.left) / r.width) * 100;
        const y = ((e.clientY - r.top) / r.height) * 100;
        el.style.setProperty("--mx", `${x}%`);
        el.style.setProperty("--my", `${y}%`);
    };

    return (
        <div ref={sectionRef} id="founder" className="grid grid-cols-12 gap-8 lg:gap-12 items-start">
            <div className="col-span-12 md:col-span-5">
                <FadeUp>
                    <motion.div
                        ref={frameRef}
                        onMouseMove={onMove}
                        data-testid="founder-portrait"
                        data-cursor="hover"
                        style={{ scale: portraitScale, aspectRatio: "4 / 5" }}
                        className="spotlight-frame rounded-2xl border border-white/10"
                    >
                        <img
                            src={FOUNDER.portrait}
                            alt={`${FOUNDER.name} — ${FOUNDER.role}`}
                            loading="lazy"
                            className="absolute inset-0 w-full h-full object-cover contrast-[1.04] brightness-[0.98] saturate-[1.05]"
                            style={{ objectPosition: "center 25%" }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                        <div
                            className="absolute inset-0 pointer-events-none"
                            style={{
                                background:
                                    "radial-gradient(ellipse at center, transparent 55%, hsla(38,92%,62%,0.06) 100%)",
                            }}
                        />
                        <div className="absolute bottom-0 left-0 right-0 p-5 z-[3]">
                            <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--text-muted))]">
                                B.S. Radhakrishnan — Founder
                            </div>
                        </div>
                    </motion.div>
                </FadeUp>
            </div>

            <div className="col-span-12 md:col-span-7">
                <MaskText
                    as="h3"
                    lines={[FOUNDER.name]}
                    testId="founder-name"
                    className="font-display text-[clamp(2.2rem,6vw,5rem)] leading-[0.95]"
                />
                <FadeUp delay={0.1}>
                    <div className="mt-3 font-mono text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))]">
                        {FOUNDER.role}
                    </div>
                </FadeUp>

                <motion.blockquote
                    ref={quoteRef}
                    data-testid="founder-pull-quote"
                    style={{ x: quoteX, opacity: quoteOpacity }}
                    className="mt-10 pl-5 border-l-2 border-[hsl(var(--accent-gold))]"
                >
                    <p className="font-display text-[clamp(1.5rem,3.2vw,2.4rem)] leading-[1.1] text-white">
                        “{FOUNDER.quote}”
                    </p>
                </motion.blockquote>

                <div className="max-w-2xl">
                    <ReadMoreBio paragraphs={FOUNDER.bio} testId="founder-bio" />
                </div>
            </div>
        </div>
    );
};

// ===== Director block (Girishh) — monogram or photo + Read more =====
const DirectorBlock = ({ person }) => {
    return (
        <div
            className="grid grid-cols-12 gap-8 lg:gap-12 items-start"
            data-testid={`team-member-${person.slug}`}
        >
            <div className="col-span-12 md:col-span-5">
                <FadeUp>
                    <div
                        className="relative rounded-2xl border border-white/10 overflow-hidden bg-black"
                        style={{ aspectRatio: "4 / 5" }}
                    >
                        {person.image ? (
                            <img
                                src={person.image}
                                alt={`${person.name} — ${person.role}`}
                                loading="lazy"
                                className="absolute inset-0 w-full h-full object-cover contrast-[1.04] brightness-[0.98] saturate-[1.05]"
                            />
                        ) : (
                            <MonogramTile letters={person.monogram} />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                        <div className="absolute bottom-0 left-0 right-0 p-5 z-[3]">
                            <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--text-muted))]">
                                {person.name} — Director
                            </div>
                        </div>
                    </div>
                </FadeUp>
            </div>
            <div className="col-span-12 md:col-span-7">
                <MaskText
                    as="h3"
                    lines={[person.name]}
                    className="font-display text-[clamp(1.8rem,4.5vw,3.6rem)] leading-[0.98]"
                />
                <FadeUp delay={0.1}>
                    <div className="mt-3 font-mono text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))]">
                        {person.role}
                    </div>
                </FadeUp>
                <ReadMoreBio paragraphs={person.bio} testId={`team-bio-${person.slug}`} />
            </div>
        </div>
    );
};

// ===== Exec card (Santosh / Pranava) — portrait + name + role + Read-more =====
const ExecCard = ({ person }) => {
    return (
        <article
            data-testid={`team-member-${person.slug}`}
            data-cursor="hover"
            className="cine-card rounded-2xl h-full bg-[hsl(var(--bg-panel))] border border-white/10 overflow-hidden flex flex-col"
        >
            {person.image && (
                <div
                    className="relative w-full overflow-hidden bg-black"
                    style={{ aspectRatio: "4 / 5" }}
                >
                    <img
                        src={person.image}
                        alt={`${person.name} — ${person.role}`}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover contrast-[1.04] brightness-[0.96] saturate-[1.05]"
                        style={{ objectPosition: "center 22%" }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                    <div
                        className="absolute inset-0 pointer-events-none"
                        style={{
                            background:
                                "radial-gradient(ellipse at center, transparent 55%, hsla(38,92%,62%,0.06) 100%)",
                        }}
                    />
                </div>
            )}
            <div className="p-7 sm:p-9 flex-1 flex flex-col">
                <h3 className="font-display text-3xl sm:text-4xl tracking-tight leading-[0.98]">
                    {person.name}
                </h3>
                <div className="mt-2 font-mono text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))]">
                    {person.role}
                </div>
                <ReadMoreBio paragraphs={person.bio} testId={`team-bio-${person.slug}`} />
            </div>
        </article>
    );
};

// ===== Read more / Read less expandable bio =====
const ReadMoreBio = ({ paragraphs, testId }) => {
    const [open, setOpen] = useState(false);
    const first = paragraphs[0];
    const rest = paragraphs.slice(1);

    return (
        <div data-testid={testId} className="mt-7">
            <p className="text-[hsl(var(--text-secondary))] leading-relaxed">{first}</p>

            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        key="rest"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                    >
                        <div className="space-y-5 mt-5">
                            {rest.map((p) => (
                                <p
                                    key={p.slice(0, 40)}
                                    className="text-[hsl(var(--text-secondary))] leading-relaxed"
                                >
                                    {p}
                                </p>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {rest.length > 0 && (
                <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    data-testid={`${testId}-toggle`}
                    className="mt-5 group inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent-gold))] hover:text-white transition-colors"
                >
                    {open ? "Read less" : "Read more"}
                    <ChevronDown
                        className={`h-3.5 w-3.5 transition-transform ${
                            open ? "rotate-180" : ""
                        }`}
                    />
                </button>
            )}
        </div>
    );
};

// ===== Monogram tile (image placeholder for Girishh until photo arrives) =====
const MonogramTile = ({ letters }) => (
    <div className="absolute inset-0 flex items-center justify-center bg-black">
        {/* faint rotating atom for brand continuity */}
        <motion.div
            aria-hidden
            animate={{ rotate: 360 }}
            transition={{ duration: 160, ease: "linear", repeat: Infinity }}
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
        >
            <svg
                viewBox="0 0 200 200"
                className="w-[78%] h-[78%] opacity-[0.07]"
                fill="none"
            >
                <ellipse cx="100" cy="100" rx="80" ry="32" stroke="hsl(0 0% 96%)" strokeWidth="1" />
                <ellipse
                    cx="100"
                    cy="100"
                    rx="80"
                    ry="32"
                    stroke="hsl(0 0% 96%)"
                    strokeWidth="1"
                    transform="rotate(60 100 100)"
                />
                <ellipse
                    cx="100"
                    cy="100"
                    rx="80"
                    ry="32"
                    stroke="hsl(0 0% 96%)"
                    strokeWidth="1"
                    transform="rotate(120 100 100)"
                />
                <circle cx="100" cy="100" r="6" fill="hsl(38 92% 62%)" />
            </svg>
        </motion.div>
        <div
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{
                background:
                    "radial-gradient(ellipse at center, transparent 35%, rgba(0,0,0,0.65) 100%)",
            }}
        />
        <div className="relative z-10 flex flex-col items-center">
            <span className="font-display text-[clamp(6rem,16vw,12rem)] tracking-tight leading-none text-white">
                {letters}
            </span>
            <span className="mt-3 font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent-gold))]">
                Portrait · Coming Soon
            </span>
        </div>
    </div>
);
